import * as React from 'react';

import {
  Toast,
  toastApi,
  ToastTitle,
} from '@spaced-out/ui-design-system/lib/components/Toast';

import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {ApiError, captureApiError, getApiErrorMessage} from 'src/utils/errors';
import {captureSentryException} from 'src/utils/sentry';

// Status codes we don't want to noise Sentry with. 401 is handled globally;
// 404 is often expected (polling for draft, etc.).
const IGNORED_STATUS_CODES = new Set<number>([401, 404]);

// Transient statuses that are safe to retry: network (0 or undefined) and 5xx.
function isTransientStatus(status: number | undefined): boolean {
  if (status == null || status === 0) {
    return true;
  }
  return status >= 500 && status < 600;
}

/**
 * Synthetic error for BYOD query/mutation failures that aren't ApiErrors —
 * e.g. backend-reported tile errors, missing-store-widget, malformed
 * responses. Carries a `retryable` hint so the retry policy can distinguish
 * "retrying might help" (transient server hiccup) from "retrying won't help"
 * (programmer error / stale store).
 */
export class BYODTileError extends Error {
  retryable: boolean;
  constructor(message: string, options?: {retryable?: boolean}) {
    super(message);
    this.name = 'BYODTileError';
    this.retryable = options?.retryable ?? false;
  }
}

/**
 * Pure classifier for BYOD errors — separate from any retry-count policy.
 * Use this when composing a custom retry function with a configurable cap;
 * otherwise prefer `byodQueryRetry` which bakes in the default 2-retry cap.
 */
export function isByodErrorRetryable(error: unknown): boolean {
  if (error instanceof ApiError) {
    return isTransientStatus(error.response?.status);
  }
  if (error instanceof BYODTileError) {
    return error.retryable;
  }
  // Unknown error shape — retry once. Most fetch-level throws land here.
  return true;
}

/**
 * React Query `retry` function for BYOD queries.
 * - Retries up to 2 times on transient (network / 5xx) errors.
 * - Does not retry on 4xx (client errors — retrying won't help).
 * - Honors `BYODTileError.retryable` for synthetic failures.
 */
export function byodQueryRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= 2) {
    return false;
  }
  return isByodErrorRetryable(error);
}

/**
 * Default React Query options for every BYOD query. Spread into `queryOptions`.
 * Individual hooks can still override.
 */
export const byodQueryDefaults = {
  retry: byodQueryRetry,
  // Small backoff — enough to avoid hammering a briefly-down endpoint, short
  // enough that the user isn't waiting long on flaky networks.
  retryDelay: (attempt: number) => Math.min(1000 * 2 ** attempt, 5000),
} as const;

/**
 * Report an API error to Sentry with BYOD feature tag, skipping noisy statuses
 * (401, 404) by default. Use this at mutation `onError` sites and anywhere we
 * swallow an error but still want it observable.
 */
export function reportByodApiError(
  error: unknown,
  context?: {action?: string; [key: string]: unknown},
): void {
  if (error instanceof ApiError) {
    const status = error.response?.status;
    if (status != null && IGNORED_STATUS_CODES.has(status)) {
      return;
    }
    captureApiError(error, {feature: 'byod'}, {...context});
    return;
  }
  // Don't crash Sentry if a non-Error value was thrown.
  const err =
    error instanceof Error
      ? error
      : new Error(String(error ?? 'Unknown error'));
  captureSentryException(err, {
    level: 'error',
    tags: {feature: 'byod'},
    extra: context ?? {},
  });
}

/**
 * Hook: returns a stable `onError` callback suitable for BYOD mutations.
 * - Reports to Sentry via `reportByodApiError`.
 * - Shows a danger toast with the server-provided message (or a fallback).
 *
 * Usage:
 *   const onError = useByodMutationErrorHandler('add widget');
 *   useApiMutation({ mutationFn, onError });
 */
export function useByodMutationErrorHandler(
  action: string,
  options?: {
    /** Override the toast title (defaults to "{action} failed. Please try again."). */
    getToastTitle?: (error: unknown) => string;
    /** Skip the toast (still reports to Sentry). Useful for background/idempotent writes. */
    silent?: boolean;
  },
): (error: Error) => undefined {
  const {t} = useLabelTranslation();
  const silent = options?.silent ?? false;
  const getToastTitle = options?.getToastTitle;

  return React.useCallback(
    (error: Error): undefined => {
      reportByodApiError(error, {action});

      if (silent) {
        return undefined;
      }

      let title: string;
      if (getToastTitle) {
        title = getToastTitle(error);
      } else {
        const serverMessage =
          error instanceof ApiError ? getApiErrorMessage(error) : undefined;
        title =
          serverMessage ??
          t(
            'Something_went_wrong_Please_try_again',
            'Something went wrong. Please try again.',
          );
      }

      toastApi.show(
        <Toast initialFocus={0} semantic="danger">
          <ToastTitle>{title}</ToastTitle>
        </Toast>,
      );

      return undefined;
    },
    [action, silent, getToastTitle, t],
  );
}
